import {
  CustomerDataProps,
  ValidateFormProps
} from "@saleor/createOrder/views/CreateOrderView";
import { makeStyles } from "@saleor/macaw-ui";
import { parseDateString } from "@saleor/utils/date";
import { useFormik } from "formik";
import React, { createContext, FC, memo, useEffect, useState } from "react";
import * as Yup from "yup";

import CustomerData from "./CustomerData";

export const CustomerContext = createContext(null);

interface CustomerInfoProps {
  customerData: CustomerDataProps;
  submitForm: (data) => void;
  handleGetCustomerInfo: (values: any) => void;
  validateForm: ValidateFormProps;
  setValidateForm: (values: ValidateFormProps) => void;
}

const CustomerInfo: FC<CustomerInfoProps> = memo(
  ({
    customerData,
    submitForm,
    handleGetCustomerInfo,
    validateForm,
    setValidateForm
  }) => {
    const [componentKey, setComponentKey] = useState<number>(0);
    const useStyles = makeStyles(
      () => ({
        root: {
          borderRadius: 6,
          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
          overflow: "hidden",
          marginTop: "1.6rem",
          height: "fit-content"
        }
      }),
      { name: "CreateOrder" }
    );

    const classes = useStyles();
    const CreateOrderSchema = Yup.object().shape({
      shipTo: Yup.string().required("Required"),
      billTo: Yup.string().required("Required"),
      orderType: Yup.string().required("Required"),
      poNumber: Yup.string().required("Required"),
      poDate: Yup.date().required("Required"),
      requestDate: Yup.date()
        .transform(parseDateString)
        .min(new Date())
        .required("Required")
    });

    const formik = useFormik({
      initialValues: {
        shipTo: "",
        billTo: "",
        poNumber: "",
        poDate: customerData?.poDate || new Date().toDateString(),
        orderType: "",
        requestDate: customerData?.requestDate,
        externalCommentsToCustomer: "",
        internalCommentsToWarehouse: "",
        internalCommentsToLogistic: "",
        productInformation: ""
      },
      onSubmit: () => {
        submitForm(formik.values);
      },
      validationSchema: CreateOrderSchema
    });

    useEffect(() => {
      if (customerData) {
        const address1 =
          customerData?.customerInfo?.representatives[0]?.addresses?.[0]
            ?.streetAddress1;
        const address2 =
          customerData?.customerInfo?.representatives[0]?.addresses?.[0]
            ?.streetAddress2;
        const addressStr = Object.values({
          address1,
          address2
        })
          .filter(i => Boolean(i))
          .join(", ");

        formik.setValues({
          poNumber: customerData?.poNumber || "",
          poDate: customerData?.poDate || new Date().toDateString(),
          internalCommentsToWarehouse:
            customerData?.internalCommentsToWarehouse || "",
          externalCommentsToCustomer:
            customerData?.externalCommentsToCustomer || "",
          internalCommentsToLogistic:
            customerData?.internalCommentsToLogistic || "",
          productInformation: customerData?.productInformation || "",
          orderType: customerData?.orderType,
          requestDate: customerData?.requestDate,
          shipTo: customerData?.shipTo || addressStr,
          billTo: customerData?.billTo || addressStr
        });
        setComponentKey(prev => prev + 1);
      }
    }, [customerData?.customerInfo]);

    useEffect(() => {
      handleGetCustomerInfo(formik.values);
    }, [formik.values]);

    useEffect(() => {
      if (validateForm?.isCheckValidate && !formik.isValid) {
        // Verify validate form
        formik.handleSubmit();
      }
      // Validate submit
      setValidateForm({
        ...validateForm,
        isValidate: { ...validateForm.isValidate, validateInfo: formik.isValid }
      });
    }, [validateForm?.isCheckValidate, formik.isValid]);

    return (
      <div className={classes.root}>
        <CustomerContext.Provider value={{ customerData, formik }}>
          <form onSubmit={formik.handleSubmit}>
            <CustomerData key={componentKey} />
          </form>
        </CustomerContext.Provider>
      </div>
    );
  }
);

export default CustomerInfo;
